/**
 * Document-review scan profiling (AIVA-8925). Off unless switched on in the
 * task pane's DevTools:
 *
 *   localStorage.setItem("aiva.docreview.profile", "1")
 *
 * Why it exists: the scan's sweeps run in parallel, and Word answers their
 * requests one at a time, so each sweep's logged time includes the time it
 * spent queued behind the others. A slow sweep and a sweep waiting for a slow
 * one look the same. With profiling on, the sweeps run one after another and
 * each logs its own time plus how many Word round trips (`context.sync()`) and
 * location comparisons (`Range.compareLocationWith`) it made — the two costs
 * that grow with a document's links, fields and TOC entries.
 *
 * Counting wraps those two Office.js methods for the life of the task pane,
 * once, and only when profiling is on. The wrappers add a counter increment and
 * nothing else; a normal scan never installs them.
 */

const PROFILE_KEY = "aiva.docreview.profile";

export interface OfficeCallCounts {
  syncs: number;
  compares: number;
}

let current: OfficeCallCounts | null = null;
let installed = false;
let serialTail: Promise<unknown> = Promise.resolve();

/** Whether this task pane asked for scan profiling. Never throws: storage can
 *  be unavailable in a locked-down host. */
export function scanProfilingEnabled(): boolean {
  try {
    return globalThis.localStorage?.getItem(PROFILE_KEY) === "1";
  } catch {
    return false;
  }
}

function wrapCounting(proto: Record<string, unknown> | undefined, method: string, key: keyof OfficeCallCounts): void {
  const original = proto?.[method];
  if (!proto || typeof original !== "function") return;
  proto[method] = function (this: unknown, ...args: unknown[]) {
    if (current) current[key] += 1;
    return (original as (...a: unknown[]) => unknown).apply(this, args);
  };
}

function installCounters(): void {
  if (installed) return;
  installed = true;
  const word = (globalThis as unknown as { Word?: { RequestContext?: { prototype: Record<string, unknown> }; Range?: { prototype: Record<string, unknown> } } }).Word;
  wrapCounting(word?.RequestContext?.prototype, "sync", "syncs");
  wrapCounting(word?.Range?.prototype, "compareLocationWith", "compares");
}

/**
 * Run `run` alone — after every sweep queued before it — and count the Word
 * calls it makes. Sweeps are queued in call order, so a wave started with
 * `Promise.all` runs one at a time without its callers changing. A failed
 * sweep does not stop the ones queued after it.
 */
export function profileSweep<T>(run: () => Promise<T>): Promise<{ result: T; counts: OfficeCallCounts }> {
  installCounters();
  const next = serialTail.then(async () => {
    const counts: OfficeCallCounts = { syncs: 0, compares: 0 };
    current = counts;
    try {
      return { result: await run(), counts };
    } finally {
      current = null;
    }
  });
  serialTail = next.catch(() => undefined);
  return next;
}

/** Time one step inside a sweep and log it — only when profiling is on, so a
 *  normal scan pays nothing. `detail` is computed after the step, so it can
 *  report sizes the step produced (AIVA-8925). */
export async function profiledStep<T>(label: string, run: () => Promise<T>, detail?: (result: T) => string): Promise<T> {
  if (!scanProfilingEnabled()) return run();
  const started = performance.now();
  const result = await run();
  const extra = detail ? ` (${detail(result)})` : "";
  console.info(`[docReview] profile:   ${label} ${Math.round(performance.now() - started)}ms${extra}`);
  return result;
}
